import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import Layout from '../components/Layout';

const PAGE_NAMES: Record<string, string> = {
  '/': 'Projects',
  '/factsagent': 'FactsAgent',
  '/icpagent': 'ICP Tool',
  '/topicalmapv2-pipeline': 'Topical Map V2',
  '/content-writer': 'Blog Posts',
  '/contentframeworkv2': 'Content Framework',
  '/cpcustom': 'CF Custom',
  '/cpimages': 'CP Images',
  '/briefcontent': 'Bulk Brief to Content',
  '/liveupdate': 'WP Live Update',
  '/payloadagent': 'Payload Agent',
  '/imagegenerator': 'Image Generator',
  '/socialmedia': 'Social Media',
  '/gdriveeditor': 'Google Drive Editor',
  '/article-update': 'Article Update',
  '/brand-brain': 'Brand Brain',
  '/llm-visibility': 'LLM Visibility',
  '/intent-volume': 'Intent Volume',
  '/holistic-seo': 'Holistic SEO',
  '/settings': 'Settings',
  '/knowledgebase': 'Knowledge Base',
  '/users': 'Users',
  '/topicalmap': 'Topical Map V1',
  '/content': 'Content',
  '/contentframework': 'CF V1',
  '/super-admin': 'Super Admin',
  '/system-monitor': 'System Monitor',
};

const AGENT_NAMES: Record<string, string> = {
  factsagent: 'FactsAgent',
  topicalmap: 'Topical Map V1',
  topicalmapv2: 'Topical Map V2',
  cppages: 'CP Pages',
  content_writer: 'Blog Posts',
  contentframeworkv2: 'Content Framework V2',
  contentframework: 'Content Framework V1',
  cpcustom: 'CF Custom',
  cpimages: 'CP Images',
  briefcontent: 'Bulk Brief',
  liveupdate: 'WP Live Update',
  payloadagent: 'Payload Agent',
  imagegenerator: 'Image Generator',
  socialmedia: 'Social Media',
  gdriveeditor: 'Google Drive',
  articleupdate: 'Article Update',
  icpagent: 'ICP Agent',
  brandbrain: 'Brand Brain',
  llmvisibility: 'LLM Visibility',
  intentvolume: 'Intent Volume',
  serviceareapages: 'Service Area Pages',
  contentgen: 'Content Gen',
  bahaagents: 'Baha Agents',
  seotools: 'SEO Tools',
};

function agentName(st: string): string {
  if (!st) return 'Unknown';
  return AGENT_NAMES[st] || st;
}

function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '0s';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

function pageName(path: string): string {
  if (!path) return 'Unknown';
  return PAGE_NAMES[path] || path;
}

function formatFlowName(flow: string): string {
  return flow.split(' → ').map(p => pageName(p.trim())).join(' → ');
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

type TabType = 'overview' | 'users' | 'heatmap' | 'user-detail' | 'agents' | 'llm-settings';

export default function SuperAdminPage() {
  const [tab, setTab] = useState<TabType>('overview');
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Existing state
  const [overview, setOverview] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [heatmap, setHeatmap] = useState<any>(null);
  const [timeline, setTimeline] = useState<any[]>([]);
  const [userDetail, setUserDetail] = useState<any>(null);
  const [pageBreakdown, setPageBreakdown] = useState<any[]>([]);
  const [activityFilter, setActivityFilter] = useState<string>('all');
  const [detailSubTab, setDetailSubTab] = useState<'overview' | 'sessions' | 'activity'>('overview');

  // Agent usage state
  const [agentUsage, setAgentUsage] = useState<any>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: any = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      if (tab === 'overview') {
        const [ovRes, tlRes, pbRes] = await Promise.all([
          api.get('/useractivity/analytics/overview', { params }),
          api.get('/useractivity/analytics/timeline', { params }),
          api.get('/useractivity/analytics/page-breakdown', { params }),
        ]);
        setOverview(ovRes.data);
        setTimeline(tlRes.data);
        setPageBreakdown(pbRes.data);
      } else if (tab === 'users') {
        const res = await api.get('/useractivity/analytics/users', { params });
        setUsers(res.data);
      } else if (tab === 'heatmap') {
        const res = await api.get('/useractivity/analytics/heatmap', { params });
        setHeatmap(res.data);
      } else if (tab === 'user-detail' && selectedUserId) {
        const res = await api.get(`/useractivity/analytics/user-detail/${selectedUserId}`, { params });
        setUserDetail(res.data);
      } else if (tab === 'agents') {
        const res = await api.get('/useractivity/analytics/agent-usage', { params });
        setAgentUsage(res.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load analytics');
    }
    setLoading(false);
  }, [tab, dateFrom, dateTo, selectedUserId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openUserDetail = (userId: number) => {
    setSelectedUserId(userId);
    setDetailSubTab('overview');
    setTab('user-detail');
  };

  const PRIMARY_TABS: { key: TabType; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'users', label: 'Users' },
    { key: 'heatmap', label: 'Heatmap' },
    { key: 'agents', label: 'Agent Usage' },
    { key: 'llm-settings', label: 'LLM Settings' },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Super Admin Analytics</h1>
          <p className="text-gray-500 text-sm mt-1">Track user activity, agent usage, system metrics, and API costs</p>
        </div>

        {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}

        {/* Tabs + Date Filter */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1 flex-wrap">
            {PRIMARY_TABS.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                {t.label}
              </button>
            ))}
            {tab === 'user-detail' && (
              <button className="px-3 py-2 text-sm font-medium rounded-md bg-white text-gray-900 shadow-sm">
                User Detail
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            <span className="text-gray-400">to</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            <button onClick={fetchData} className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm hover:bg-brand-700">
              Refresh
            </button>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <span className="spinner mr-2"></span>
            <span className="text-gray-500">Loading analytics...</span>
          </div>
        )}

        {/* ═══════════════ OVERVIEW TAB ═══════════════ */}
        {!loading && tab === 'overview' && overview && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Active Users" value={overview.active_users} icon="users" />
              <StatCard label="Page Views" value={overview.total_page_views.toLocaleString()} icon="eye" />
              <StatCard label="Total Actions" value={overview.total_events.toLocaleString()} icon="zap" />
              <StatCard label="Total Time" value={formatDuration(overview.total_time_seconds)} icon="clock" />
            </div>

            {timeline.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Activity</h3>
                <div className="flex items-end gap-px h-40 overflow-x-auto">
                  {timeline.map((day: any) => {
                    const total = day.page_views + day.actions;
                    const maxVal = Math.max(...timeline.map((d: any) => d.page_views + d.actions), 1);
                    const height = Math.max((total / maxVal) * 100, 2);
                    return (
                      <div key={day.date} className="flex-1 min-w-[8px] flex flex-col items-center group relative">
                        <div className="w-full bg-brand-500 rounded-t hover:bg-brand-600 transition-colors cursor-pointer"
                          style={{ height: `${height}%` }}
                          title={`${day.date}: ${day.page_views} views, ${day.actions} actions`} />
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 hidden group-hover:block bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                          {day.date}: {total} events
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-2 text-xs text-gray-400">
                  <span>{timeline[0]?.date}</span>
                  <span>{timeline[timeline.length - 1]?.date}</span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Time Spent by Tool</h3>
                <div className="space-y-3">
                  {pageBreakdown.slice(0, 12).map((p: any) => {
                    const maxTime = pageBreakdown[0]?.total_seconds || 1;
                    const pct = (p.total_seconds / maxTime) * 100;
                    return (
                      <div key={p.page}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium text-gray-700">{pageName(p.page)}</span>
                          <span className="text-gray-500">{formatDuration(p.total_seconds)} ({p.unique_users} users)</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-brand-500 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Pages by Views</h3>
                <div className="space-y-3">
                  {overview.top_pages.slice(0, 12).map((p: any) => {
                    const maxViews = overview.top_pages[0]?.views || 1;
                    const pct = (p.views / maxViews) * 100;
                    return (
                      <div key={p.page}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium text-gray-700">{pageName(p.page)}</span>
                          <span className="text-gray-500">{p.views} views</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {overview.top_actions.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Actions</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {overview.top_actions.map((a: any) => (
                    <div key={a.action} className="bg-gray-50 rounded-lg px-4 py-3">
                      <p className="text-sm font-medium text-gray-700">{a.action || 'unknown'}</p>
                      <p className="text-xl font-bold text-gray-900">{a.count}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══════════════ USERS TAB ═══════════════ */}
        {!loading && tab === 'users' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Page Views</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Time Spent</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Most Used Tool</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Active</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((u: any) => (
                  <tr key={u.user_id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openUserDetail(u.user_id)}>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900">{u.name || 'No name'}</p>
                      <p className="text-xs text-gray-500">{u.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                        u.is_super_admin ? 'bg-amber-100 text-amber-800' :
                        u.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                        'bg-blue-100 text-blue-700'}`}>
                        {u.is_super_admin ? 'super admin' : u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-700">{u.page_views}</td>
                    <td className="px-4 py-3 text-right text-sm text-gray-700">{u.actions}</td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">{formatDuration(u.time_spent_seconds)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{u.top_page ? pageName(u.top_page) : '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {u.last_active ? new Date(u.last_active).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={(e) => { e.stopPropagation(); openUserDetail(u.user_id); }}
                        className="text-brand-600 hover:text-brand-700 text-sm font-medium">Details</button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No activity data found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* ═══════════════ HEATMAP TAB ═══════════════ */}
        {!loading && tab === 'heatmap' && heatmap && (
          <HeatmapGrid heatmap={heatmap} title="Platform Activity Heatmap" />
        )}

        {/* ═══════════════ USER DETAIL TAB ═══════════════ */}
        {!loading && tab === 'user-detail' && userDetail && (
          <UserDetailView
            data={userDetail}
            onBack={() => setTab('users')}
            detailSubTab={detailSubTab}
            setDetailSubTab={setDetailSubTab}
            activityFilter={activityFilter}
            setActivityFilter={setActivityFilter}
          />
        )}

        {/* ═══════════════ AGENT USAGE TAB ═══════════════ */}
        {!loading && tab === 'agents' && agentUsage && (
          <AgentUsageView data={agentUsage} />
        )}

        {tab === 'llm-settings' && <LLMSettingsView />}

      </div>
    </Layout>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   AGENT USAGE VIEW
   ═══════════════════════════════════════════════════════════════════════════ */

function AgentUsageView({ data }: { data: any }) {
  const { agent_stats, user_agent_breakdown, recent_jobs } = data;
  const totalJobs = agent_stats.reduce((s: number, a: any) => s + a.total_jobs, 0);

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <MiniStat label="Total Jobs" value={totalJobs} color="brand" />
        <MiniStat label="Agents Used" value={agent_stats.length} color="blue" />
        <MiniStat label="Total Completed" value={agent_stats.reduce((s: number, a: any) => s + a.completed, 0)} color="green" />
        <MiniStat label="Total Failed" value={agent_stats.reduce((s: number, a: any) => s + a.failed, 0)} color="amber" />
      </div>

      {/* Per-agent breakdown */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Jobs by Agent</h3>
        </div>
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Jobs</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Completed</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Failed</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">In Progress</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Users</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Success Rate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {agent_stats.map((a: any) => {
              const rate = a.total_jobs > 0 ? ((a.completed / a.total_jobs) * 100).toFixed(0) : '0';
              return (
                <tr key={a.system_type} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-gray-900">{agentName(a.system_type)}</span>
                    <span className="text-xs text-gray-400 ml-2">{a.system_type}</span>
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">{a.total_jobs}</td>
                  <td className="px-4 py-3 text-right text-sm text-green-600">{a.completed}</td>
                  <td className="px-4 py-3 text-right text-sm text-red-600">{a.failed}</td>
                  <td className="px-4 py-3 text-right text-sm text-amber-600">{a.processing}</td>
                  <td className="px-4 py-3 text-right text-sm text-gray-700">{a.unique_users}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden max-w-[80px]">
                        <div className={`h-full rounded-full ${Number(rate) >= 80 ? 'bg-green-500' : Number(rate) >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                          style={{ width: `${rate}%` }} />
                      </div>
                      <span className="text-xs text-gray-500">{rate}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* User-Agent Breakdown */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Jobs by User & Agent</h3>
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Jobs</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {user_agent_breakdown.map((r: any, i: number) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm text-gray-700">{r.name}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{agentName(r.system_type)}</td>
                  <td className="px-4 py-2 text-right text-sm font-medium text-gray-900">{r.jobs}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Jobs */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent Jobs</h3>
        </div>
        <div className="max-h-[500px] overflow-y-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Progress</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {recent_jobs.map((j: any) => (
                <tr key={j.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm font-mono text-gray-900">#{j.id}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{agentName(j.system_type)}</td>
                  <td className="px-4 py-2 text-sm text-gray-700 max-w-[150px] truncate" title={j.project_name || '-'}>{j.project_name || '-'}</td>
                  <td className="px-4 py-2">
                    <p className="text-sm text-gray-700">{j.user_name}</p>
                    <p className="text-xs text-gray-400">{j.user_email}</p>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                      j.status === 'completed' ? 'bg-green-100 text-green-700' :
                      j.status === 'failed' ? 'bg-red-100 text-red-700' :
                      j.status === 'processing' ? 'bg-amber-100 text-amber-700' :
                      'bg-gray-100 text-gray-600'}`}>
                      {j.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right text-sm text-gray-500">
                    {j.completed_items ?? 0}/{j.total_items ?? '?'} ({j.progress}%)
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">
                    {j.created_at ? new Date(j.created_at).toLocaleString() : '-'}
                  </td>
                  <td className="px-4 py-2 text-xs text-red-500 max-w-[200px] truncate" title={j.error_message || ''}>
                    {j.error_message || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   USER DETAIL VIEW — Comprehensive user analytics
   ═══════════════════════════════════════════════════════════════════════════ */

function UserDetailView({ data, onBack, detailSubTab, setDetailSubTab, activityFilter, setActivityFilter }: {
  data: any;
  onBack: () => void;
  detailSubTab: string;
  setDetailSubTab: (t: any) => void;
  activityFilter: string;
  setActivityFilter: (f: string) => void;
}) {
  const { user, summary, page_time, actions, heatmap: userHeatmap, daily_timeline, sessions, nav_flows, projects_used, never_used, ip_addresses, user_agents, recent } = data;

  return (
    <div className="space-y-6">
      {/* Header: Back + User card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center">
              <span className="text-white text-lg font-bold">{(user.name || user.email || 'U').charAt(0).toUpperCase()}</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{user.name || user.email}</h2>
              <p className="text-sm text-gray-500">{user.email}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                  user.is_super_admin ? 'bg-amber-100 text-amber-800' :
                  user.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                  'bg-blue-100 text-blue-700'}`}>
                  {user.is_super_admin ? 'super admin' : user.role}
                </span>
                {user.created_at && (
                  <span className="text-xs text-gray-400">Member since {new Date(user.created_at).toLocaleDateString()}</span>
                )}
              </div>
            </div>
          </div>
          <div className="text-right text-sm text-gray-500">
            {summary.first_seen && <p>First seen: {new Date(summary.first_seen).toLocaleDateString()}</p>}
            {summary.last_seen && <p>Last seen: {timeAgo(summary.last_seen)}</p>}
            {user.last_login && <p>Last login: {new Date(user.last_login).toLocaleString()}</p>}
          </div>
        </div>
      </div>

      {/* Stat Cards Row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <MiniStat label="Total Time" value={formatDuration(summary.total_time_seconds)} color="brand" />
        <MiniStat label="Avg Daily" value={formatDuration(summary.avg_daily_time_seconds)} color="indigo" />
        <MiniStat label="Page Views" value={summary.total_page_views} color="blue" />
        <MiniStat label="Actions" value={summary.total_actions} color="green" />
        <MiniStat label="Active Days" value={`${summary.active_days}/${summary.date_range_days}`} color="purple" />
        <MiniStat label="Engagement" value={`${summary.engagement_rate}%`} color="amber" />
      </div>

      {/* Second row of stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MiniStat label="Sessions" value={summary.total_sessions} color="gray" />
        <MiniStat label="Avg Session" value={`${summary.avg_session_minutes}m`} color="gray" />
        <MiniStat label="Peak Hour" value={`${summary.peak_hour}:00`} color="gray" />
        <MiniStat label="Peak Day" value={summary.peak_day} color="gray" />
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        {(['overview', 'sessions', 'activity'] as const).map(t => (
          <button key={t} onClick={() => setDetailSubTab(t)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              detailSubTab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t === 'overview' ? 'Overview' : t === 'sessions' ? 'Sessions' : 'Activity Log'}
          </button>
        ))}
      </div>

      {/* ─── OVERVIEW SUB-TAB ─── */}
      {detailSubTab === 'overview' && (
        <div className="space-y-6">
          {/* Daily Timeline */}
          {daily_timeline.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Daily Activity & Time</h3>
              <div className="flex items-end gap-px h-32 overflow-x-auto mb-1">
                {daily_timeline.map((day: any) => {
                  const maxTime = Math.max(...daily_timeline.map((d: any) => d.time_seconds), 1);
                  const maxEvents = Math.max(...daily_timeline.map((d: any) => d.events), 1);
                  const timeH = Math.max((day.time_seconds / maxTime) * 100, 0);
                  const evtH = Math.max((day.events / maxEvents) * 100, 2);
                  return (
                    <div key={day.date} className="flex-1 min-w-[6px] flex flex-col items-center group relative gap-0.5">
                      <div className="w-full bg-brand-400 rounded-t opacity-60" style={{ height: `${timeH * 0.5}%` }} />
                      <div className="w-full bg-brand-600 rounded-t" style={{ height: `${evtH * 0.5}%` }} />
                      <div className="absolute -top-10 left-1/2 -translate-x-1/2 hidden group-hover:block bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                        {day.date}: {day.events} events, {formatDuration(day.time_seconds)}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-xs text-gray-400">
                <span>{daily_timeline[0]?.date}</span>
                <div className="flex gap-4">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-brand-600 inline-block" /> Events</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-brand-400 opacity-60 inline-block" /> Time</span>
                </div>
                <span>{daily_timeline[daily_timeline.length - 1]?.date}</span>
              </div>
            </div>
          )}

          {/* Time per Tool + Personal Heatmap side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Time by Tool */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Time Spent by Tool</h3>
              {page_time.length > 0 ? (
                <div className="space-y-3">
                  {page_time.map((p: any) => {
                    const totalAll = page_time.reduce((s: number, x: any) => s + x.total_seconds, 0) || 1;
                    const pct = (p.total_seconds / (page_time[0]?.total_seconds || 1)) * 100;
                    const share = ((p.total_seconds / totalAll) * 100).toFixed(1);
                    return (
                      <div key={p.page}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium text-gray-700">{pageName(p.page)}</span>
                          <span className="text-gray-500">
                            {formatDuration(p.total_seconds)}
                            <span className="text-gray-400 ml-1">({share}%)</span>
                          </span>
                        </div>
                        <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-brand-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">{p.sessions} sessions</p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">No time tracking data yet</p>
              )}
            </div>

            {/* Personal Heatmap */}
            {userHeatmap && (
              <HeatmapGrid heatmap={userHeatmap} title="Personal Activity Pattern" compact />
            )}
          </div>

          {/* Navigation Flow + Actions side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Navigation Flow */}
            {nav_flows.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-base font-semibold text-gray-900 mb-4">Navigation Patterns</h3>
                <p className="text-xs text-gray-400 mb-3">Most common page transitions</p>
                <div className="space-y-2">
                  {nav_flows.map((f: any, i: number) => (
                    <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-gray-50 last:border-0">
                      <span className="text-gray-700 font-mono text-xs">{formatFlowName(f.flow)}</span>
                      <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-full ml-2 whitespace-nowrap">{f.count}x</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions Breakdown */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Actions Performed</h3>
              {actions.length > 0 ? (
                <div className="grid grid-cols-2 gap-2">
                  {actions.map((a: any, i: number) => (
                    <div key={i} className="bg-gray-50 rounded-lg px-3 py-2.5">
                      <p className="text-sm font-medium text-gray-700 truncate">{a.detail || 'unknown'}</p>
                      <p className="text-xs text-gray-400 truncate">{pageName(a.page)}</p>
                      <p className="text-lg font-bold text-gray-900 mt-0.5">{a.count}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">No actions recorded yet</p>
              )}
            </div>
          </div>

          {/* Projects Used */}
          {projects_used && projects_used.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Projects Used</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {projects_used.map((p: any) => (
                  <div key={p.id} className="bg-gray-50 rounded-lg px-4 py-3">
                    <p className="text-sm font-medium text-gray-800 truncate" title={p.name}>{p.name}</p>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="text-xs text-gray-500">{p.jobs} job{p.jobs !== 1 ? 's' : ''}</span>
                      {p.completed > 0 && <span className="text-xs text-green-600">{p.completed} done</span>}
                      {p.failed > 0 && <span className="text-xs text-red-500">{p.failed} failed</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Never Used Tools + Device/IP Info */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Never Used */}
            {never_used.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-base font-semibold text-gray-900 mb-2">Tools Never Used</h3>
                <p className="text-xs text-gray-400 mb-3">These tools have not been visited by this user</p>
                <div className="flex flex-wrap gap-2">
                  {never_used.map((p: string) => (
                    <span key={p} className="inline-flex px-3 py-1.5 text-xs font-medium bg-red-50 text-red-600 rounded-lg">
                      {pageName(p)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Device & IP Info */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Access Details</h3>
              {ip_addresses.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">IP Addresses</p>
                  <div className="space-y-1">
                    {ip_addresses.map((ip: any, i: number) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="font-mono text-gray-700">{ip.ip}</span>
                        <span className="text-gray-400">{ip.count} requests</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {user_agents.length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Browsers / Devices</p>
                  <div className="space-y-1">
                    {user_agents.map((ua: any, i: number) => (
                      <div key={i} className="text-xs text-gray-600 bg-gray-50 rounded px-2 py-1.5 truncate" title={ua.ua}>
                        {extractBrowser(ua.ua)} <span className="text-gray-400">({ua.count})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {ip_addresses.length === 0 && user_agents.length === 0 && (
                <p className="text-gray-400 text-sm">No access data recorded yet</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── SESSIONS SUB-TAB ─── */}
      {detailSubTab === 'sessions' && (
        <div className="space-y-4">
          {sessions.length > 0 ? sessions.map((s: any, i: number) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-sm font-bold">
                    {i + 1}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(s.start).toLocaleDateString()} {new Date(s.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {' — '}
                      {new Date(s.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    <p className="text-xs text-gray-500">
                      Duration: {formatDuration(s.duration_seconds)} | {s.page_views} page views | {s.actions} actions
                    </p>
                  </div>
                </div>
                <span className="text-xs text-gray-400">{timeAgo(s.start)}</span>
              </div>
              {/* Page flow visualization */}
              {s.pages.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  {s.pages.map((p: string, pi: number) => (
                    <span key={pi} className="flex items-center gap-1.5">
                      <span className="inline-flex px-2.5 py-1 text-xs font-medium bg-brand-50 text-brand-700 rounded-md">
                        {pageName(p)}
                      </span>
                      {pi < s.pages.length - 1 && (
                        <svg className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )) : (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400">
              No sessions found for this date range
            </div>
          )}
        </div>
      )}

      {/* ─── ACTIVITY LOG SUB-TAB ─── */}
      {detailSubTab === 'activity' && (
        <div className="bg-white rounded-xl border border-gray-200">
          {/* Filter bar */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
            <span className="text-xs text-gray-500 mr-2">Filter:</span>
            {['all', 'page_view', 'action', 'heartbeat'].map(f => (
              <button key={f} onClick={() => setActivityFilter(f)}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                  activityFilter === f ? 'bg-brand-100 text-brand-700 font-medium' : 'bg-gray-100 text-gray-500 hover:text-gray-700'
                }`}>
                {f === 'all' ? 'All' : f === 'page_view' ? 'Page Views' : f === 'action' ? 'Actions' : 'Heartbeats'}
              </button>
            ))}
            <span className="ml-auto text-xs text-gray-400">
              {recent.filter((a: any) => activityFilter === 'all' || a.action === activityFilter).length} events
            </span>
          </div>
          <div className="max-h-[600px] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 w-44">Time</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 w-24">Type</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Page</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Project</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Detail</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 w-20">Duration</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 w-28">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {recent
                  .filter((a: any) => activityFilter === 'all' || a.action === activityFilter)
                  .map((a: any) => (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">
                      {a.created_at ? new Date(a.created_at).toLocaleString() : '-'}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                        a.action === 'page_view' ? 'bg-blue-100 text-blue-700' :
                        a.action === 'action' ? 'bg-green-100 text-green-700' :
                        a.action === 'heartbeat' ? 'bg-gray-100 text-gray-500' :
                        'bg-gray-100 text-gray-600'}`}>
                        {a.action}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">{pageName(a.page)}</td>
                    <td className="px-4 py-2 text-sm text-gray-600 max-w-[140px] truncate" title={a.project_name || '-'}>{a.project_name || '-'}</td>
                    <td className="px-4 py-2 text-sm text-gray-500">{a.detail || '-'}</td>
                    <td className="px-4 py-2 text-xs text-gray-400">{a.duration_seconds ? `${a.duration_seconds}s` : '-'}</td>
                    <td className="px-4 py-2 text-xs text-gray-400 font-mono">{a.ip_address || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══════════════ SHARED COMPONENTS ═══════════════ */

function HeatmapGrid({ heatmap, title, compact }: { heatmap: any; title: string; compact?: boolean }) {
  const maxVal = Math.max(...heatmap.data.flat(), 1);
  return (
    <div className={`bg-white rounded-xl border border-gray-200 p-${compact ? '4' : '6'}`}>
      <h3 className={`${compact ? 'text-base' : 'text-lg'} font-semibold text-gray-900 mb-4`}>{title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              <th className="w-12"></th>
              {heatmap.hours.map((h: number) => (
                <th key={h} className="text-[10px] text-gray-400 font-normal px-0.5 text-center">
                  {h % 3 === 0 ? h.toString().padStart(2, '0') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {heatmap.days.map((day: string, dayIdx: number) => (
              <tr key={day}>
                <td className="text-xs text-gray-600 font-medium pr-2 py-0.5">{day.slice(0, 3)}</td>
                {heatmap.data[dayIdx].map((val: number, hourIdx: number) => {
                  const intensity = val / maxVal;
                  const bg = val === 0 ? 'bg-gray-50'
                    : intensity < 0.25 ? 'bg-brand-100'
                    : intensity < 0.5 ? 'bg-brand-300'
                    : intensity < 0.75 ? 'bg-brand-500'
                    : 'bg-brand-700';
                  return (
                    <td key={hourIdx} className="p-0.5">
                      <div className={`w-full ${compact ? 'h-5' : 'h-7'} rounded-sm ${bg} cursor-pointer transition-all hover:ring-2 hover:ring-brand-400`}
                        title={`${day} ${hourIdx}:00 — ${val} events`} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-2 mt-3 justify-end">
        <span className="text-[10px] text-gray-500">Less</span>
        <div className="w-4 h-4 rounded-sm bg-gray-50 border border-gray-200" />
        <div className="w-4 h-4 rounded-sm bg-brand-100" />
        <div className="w-4 h-4 rounded-sm bg-brand-300" />
        <div className="w-4 h-4 rounded-sm bg-brand-500" />
        <div className="w-4 h-4 rounded-sm bg-brand-700" />
        <span className="text-[10px] text-gray-500">More</span>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon }: { label: string; value: string | number; icon: string }) {
  const icons: Record<string, JSX.Element> = {
    users: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
    eye: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>,
    zap: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
    clock: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center gap-3 mb-2">
        <div className="text-brand-500">{icons[icon]}</div>
        <span className="text-sm text-gray-500">{label}</span>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   LLM SETTINGS VIEW
   ═══════════════════════════════════════════════════════════════════════════ */

const PROVIDER_COLORS: Record<string, string> = {
  anthropic: 'border-orange-300 bg-orange-50',
  openai: 'border-green-300 bg-green-50',
  google: 'border-blue-300 bg-blue-50',
  mistral: 'border-purple-300 bg-purple-50',
  groq: 'border-red-300 bg-red-50',
};

const PROVIDER_ICONS: Record<string, string> = {
  anthropic: 'A',
  openai: 'O',
  google: 'G',
  mistral: 'M',
  groq: 'Q',
};

interface ProviderConfig {
  id: number;
  provider_name: string;
  display_name: string;
  has_key: boolean;
  is_active: boolean;
  default_model: string | null;
  models: string[];
}

function LLMSettingsView() {
  const [providers, setProviders] = useState<ProviderConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<number | null>(null);
  const [testing, setTesting] = useState<number | null>(null);
  const [testResult, setTestResult] = useState<Record<number, { success: boolean; message: string }>>({});
  const [editingKey, setEditingKey] = useState<Record<number, string>>({});
  const [showKey, setShowKey] = useState<Record<number, boolean>>({});

  const fetchProviders = useCallback(async () => {
    try {
      const res = await api.get('/llm-settings/providers');
      setProviders(res.data);
    } catch (err: any) {
      console.error('Failed to load providers:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchProviders(); }, [fetchProviders]);

  const updateProvider = async (id: number, data: any) => {
    setSaving(id);
    try {
      await api.put(`/llm-settings/providers/${id}`, data);
      await fetchProviders();
      // Clear the key input after save
      setEditingKey(prev => { const n = { ...prev }; delete n[id]; return n; });
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Update failed');
    }
    setSaving(null);
  };

  const testProvider = async (id: number) => {
    setTesting(id);
    setTestResult(prev => { const n = { ...prev }; delete n[id]; return n; });
    try {
      const res = await api.post(`/llm-settings/providers/${id}/test`);
      setTestResult(prev => ({ ...prev, [id]: res.data }));
    } catch (err: any) {
      setTestResult(prev => ({
        ...prev,
        [id]: { success: false, message: err.response?.data?.detail || 'Test failed' },
      }));
    }
    setTesting(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <span className="spinner mr-2"></span>
        <span className="text-gray-500">Loading LLM providers...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">LLM Provider API Keys</h2>
        <p className="text-sm text-gray-500 mb-4">
          Manage API keys for all AI providers. Active providers with keys will be used by all agents system-wide.
          Keys stored in DB override .env file settings.
        </p>

        <div className="grid gap-4">
          {providers.map(prov => (
            <div key={prov.id}
              className={`rounded-xl border-2 p-5 transition-all ${PROVIDER_COLORS[prov.provider_name] || 'border-gray-200 bg-gray-50'} ${prov.is_active ? 'ring-2 ring-brand-500 ring-offset-1' : 'opacity-80'}`}>

              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center font-bold text-lg text-gray-700 shadow-sm">
                    {PROVIDER_ICONS[prov.provider_name] || '?'}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{prov.display_name}</h3>
                    <p className="text-xs text-gray-500">
                      {prov.has_key ? (
                        <span className="text-green-600 font-medium">Key configured</span>
                      ) : (
                        <span className="text-gray-400">No API key set</span>
                      )}
                      {prov.is_active && <span className="ml-2 text-brand-600 font-medium">Active</span>}
                    </p>
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-xs font-medium text-gray-600">Active</span>
                  <div className="relative">
                    <input type="checkbox" className="sr-only"
                      checked={prov.is_active}
                      onChange={() => updateProvider(prov.id, { is_active: !prov.is_active })} />
                    <div className={`w-10 h-5 rounded-full transition-colors ${prov.is_active ? 'bg-brand-600' : 'bg-gray-300'}`}></div>
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${prov.is_active ? 'translate-x-5' : ''}`}></div>
                  </div>
                </label>
              </div>

              {/* API Key Input */}
              <div className="flex gap-2 mb-3">
                <div className="flex-1 relative">
                  <input
                    type={showKey[prov.id] ? 'text' : 'password'}
                    placeholder={prov.has_key ? '••••••••••••••••••••••' : 'Enter API key...'}
                    value={editingKey[prov.id] ?? ''}
                    onChange={e => setEditingKey(prev => ({ ...prev, [prov.id]: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 pr-10"
                  />
                  <button
                    onClick={() => setShowKey(prev => ({ ...prev, [prov.id]: !prev[prov.id] }))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title={showKey[prov.id] ? 'Hide' : 'Show'}>
                    {showKey[prov.id] ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" /></svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
                <button
                  onClick={() => {
                    const keyVal = editingKey[prov.id];
                    if (keyVal !== undefined && keyVal !== '') {
                      updateProvider(prov.id, { api_key: keyVal });
                    }
                  }}
                  disabled={saving === prov.id || !editingKey[prov.id]}
                  className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap">
                  {saving === prov.id ? 'Saving...' : 'Save Key'}
                </button>
                {prov.has_key && (
                  <button
                    onClick={() => updateProvider(prov.id, { api_key: '' })}
                    className="px-3 py-2 text-red-600 border border-red-200 rounded-lg text-sm hover:bg-red-50"
                    title="Remove key">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                )}
              </div>

              {/* Model Selector + Test */}
              <div className="flex gap-2 items-center">
                <select
                  value={prov.default_model || ''}
                  onChange={e => updateProvider(prov.id, { default_model: e.target.value })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
                  {prov.models.map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
                <button
                  onClick={() => testProvider(prov.id)}
                  disabled={!prov.has_key || testing === prov.id}
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap">
                  {testing === prov.id ? (
                    <span className="flex items-center gap-1"><span className="spinner-sm"></span> Testing...</span>
                  ) : 'Test Connection'}
                </button>
              </div>

              {/* Test Result */}
              {testResult[prov.id] && (
                <div className={`mt-3 px-3 py-2 rounded-lg text-sm ${testResult[prov.id].success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {testResult[prov.id].success ? '✓' : '✗'} {testResult[prov.id].message}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <h3 className="font-medium text-blue-900 mb-1">How it works</h3>
        <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
          <li>API keys saved here override the .env file keys for all agents</li>
          <li>Only <strong>active</strong> providers with a key will be used</li>
          <li>The default model is used when agents don't specify a model</li>
          <li>All API calls are tracked automatically in System Monitor → API Calls &amp; Cost</li>
          <li>Use "Test Connection" to verify a key works before activating</li>
        </ul>
      </div>
    </div>
  );
}


function MiniStat({ label, value, color }: { label: string; value: string | number; color: string }) {
  const colorMap: Record<string, string> = {
    brand: 'bg-brand-50 border-brand-200 text-brand-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
  };
  return (
    <div className={`rounded-xl border px-4 py-3 ${colorMap[color] || colorMap.gray}`}>
      <p className="text-xs opacity-70 mb-0.5">{label}</p>
      <p className="text-lg font-bold">{value}</p>
    </div>
  );
}

function extractBrowser(ua: string): string {
  if (!ua) return 'Unknown';
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  return ua.slice(0, 40) + '...';
}
